import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createSession, getSession, clearSession, isAuthenticated, UserSession } from '@/lib/session';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  user: UserSession | null;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Check for existing session on mount
  useEffect(() => {
    const session = getSession();
    if (session) {
      setUser(session);
    }
    setIsLoading(false);
  }, []);

  // Auto-refresh session before expiration
  useEffect(() => {
    if (!user) return;

    const checkSession = setInterval(() => {
      const session = getSession();
      
      if (!session) {
        // Session expired
        setUser(null);
        navigate('/login');
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(checkSession);
  }, [user, navigate]);

  const login = async (email: string, _password: string, rememberMe: boolean = false) => {
    try {
      // In a real app, this would be an API call
      // For demo purposes, we'll create a mock session
      const mockUser: UserSession = {
        userId: '1',
        username: email.split('@')[0],
        email: email,
        role: 'admin',
        token: 'mock_jwt_token_' + Date.now(),
        expiresAt: Date.now() + 3600000 // 1 hour
      };

      createSession(mockUser, rememberMe);
      setUser(mockUser);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    clearSession();
    setUser(null);
    navigate('/login');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        logout,
        isAuthenticated: isAuthenticated()
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
